import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { addDays, addMonths, addWeeks, addYears, format } from 'date-fns';
import equal from 'fast-deep-equal';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { displayAutoId } from 'lib/fields';
import { dateToNumber } from 'lib/riskbase';
import { toNumber } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { GlobalFieldRepeatableInstanceScreen } from 'screens/FieldComponents/GlobalFieldRepeatableInstanceScreen';
import {
  ASSET_SAVE_REPEATABLE_FIELDS,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import {
  selectAssetFieldRepeatableInstancesForGroup,
  selectAssetFieldRepeatableNextAutoIdForGroup,
  selectAssetOrganisationId,
} from 'store/assets/selectors';
import { FieldTypes } from 'store/assets/types';
import { selectOrganisationFieldGroup } from 'store/organisations/selectors';
import { FieldField } from 'store/organisations/types';
import { PinNew } from 'store/plan/types';
import {
  AppContextEnum,
  FieldInstanceWithFormattedAutoId,
  FieldResult,
  TimescalePeriod,
} from 'types/types';
import { v4 as uuid } from 'uuid';

const createDefaultInstance = (fields: Record<string, FieldField>) =>
  Object.values(fields).reduce<Record<string, FieldResult>>((acc, f) => {
    let value = f.defaultValue;
    if (value) {
      if (f.type === FieldTypes.DATE) {
        let dateValue = new Date();
        if (value !== 'today') {
          if (value.includes(TimescalePeriod.day)) {
            dateValue = addDays(
              dateValue,
              toNumber(value.replace(TimescalePeriod.day, '')),
            );
          } else if (value.includes(TimescalePeriod.week)) {
            dateValue = addWeeks(
              dateValue,
              toNumber(value.replace(TimescalePeriod.week, '')),
            );
          } else if (value.includes(TimescalePeriod.month)) {
            dateValue = addMonths(
              dateValue,
              toNumber(value.replace(TimescalePeriod.month, '')),
            );
          } else if (value.includes(TimescalePeriod.year)) {
            dateValue = addYears(
              dateValue,
              toNumber(value.replace(TimescalePeriod.year, '')),
            );
          }
        }
        value = format(dateValue, 'yyyy-MM-dd');
      }
      acc[f.id] = {
        fieldId: f.id,
        ts: dateToNumber(),
        value,
        pin: '',
        action: null,
      };
    }
    return acc;
  }, {});

export const FieldRepeatableInstanceScreenNew: React.FC<
  ModalScreenProps<ModalScreens.NEWREPEATABLEINSTANCE>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const { assetId, groupId, initValues } = route.params;

  const organisationId = useSelector((state) =>
    selectAssetOrganisationId(state, assetId),
  );

  const group = useSelector((state) =>
    selectOrganisationFieldGroup(state, organisationId, groupId),
  );

  const allInstances = Object.values(
    useSelector((state) =>
      selectAssetFieldRepeatableInstancesForGroup(state, assetId, groupId),
    ),
  );

  const nextAutoId = useSelector((state) =>
    selectAssetFieldRepeatableNextAutoIdForGroup(state, assetId, groupId),
  );

  const [instance, setInstance] =
    React.useState<FieldInstanceWithFormattedAutoId>({
      instanceId: uuid(),
      groupId,
      createdTs: dateToNumber(),
      updatedTs: dateToNumber(),
      autoId: nextAutoId,
      results: group ? createDefaultInstance(group.fields) : {},
      action: null,
      inAssessment: false,
      reference: '',
      formattedAutoId: displayAutoId(
        nextAutoId,
        group?.autoIdZeroFill,
        group?.autoIdPrefix,
      ),
    });

  const initResultsRef = React.useRef({ ...instance.results });
  const isDirty = React.useRef(false);
  isDirty.current = !equal(initResultsRef.current, instance.results);

  const handleActualSave = () => {
    dispatch(
      ASSET_SAVE_REPEATABLE_FIELDS({
        assetId,
        instance,
      }),
    );
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleSave = React.useCallback(
    (results: Record<string, FieldResult> | null) => {
      setInstance((existing) => ({
        ...existing,
        results: { ...existing.results, ...results },
      }));
    },
    [],
  );

  const handleChange = React.useCallback(
    (
      fieldId: string,
      value: string | string[] | null,
      pin?: PinNew | '' | null,
    ) =>
      handleSave({
        [fieldId]: {
          fieldId,
          ts: dateToNumber(),
          value: value || value === '0' ? value : '',
          pin: pin || '',
          action: null,
        },
      }),
    [handleSave],
  );

  const handleClose = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: handleActualSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);

  React.useEffect(() => {
    if (initValues) {
      const newResults = Object.entries(initValues).reduce<
        FieldInstanceWithFormattedAutoId['results']
      >((acc, [initFieldId, initValue]) => {
        acc[initFieldId] = {
          fieldId: initFieldId,
          ts: dateToNumber(),
          value: initValue,
          pin: '',
          action: null,
        };
        return acc;
      }, {});
      handleSave(newResults);
    }
  }, [handleSave, initValues]);

  useEsc(handleClose);

  React.useEffect(() => {
    if (!group) {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  }, [dispatch, group]);

  if (!group) {
    return null;
  }

  return (
    <GlobalFieldRepeatableInstanceScreen
      context={{
        context: AppContextEnum.localAsset,
        assetId,
      }}
      group={group}
      fields={group.fields}
      editableFieldIds={group.fieldIds}
      id={instance.instanceId}
      reference={instance.reference}
      autoId={instance.autoId}
      otherInstances={allInstances}
      autoIdNext={0}
      fieldKeys={{}}
      // createdTs={instance.created_ts}
      onChange={handleChange}
      showInvalid={false}
      results={instance.results}
      onPressSaveButton={handleActualSave}
      onPressCancelButton={handleClose}
    />
  );
};
